import { base64ToBlob } from '@/utils/base64ToBlob';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { tetriFromDecimal } from '@/utils/money';
import { CategoriesEndpoints } from './categoriesEndpoints';
import type { ICategoriesService } from './ICategoriesService';
import type {
    CategoryAttributesWriteRequest,
    CategoryCreateRequest,
    CategoryWriteRequest,
} from './requests';
import type {
    CategoryWriteResponse,
    GetCategoryAttributesResponse,
    GetCategoryDetailsResponse,
    GetCategoryListResponse,
    GetCategoryPickerResponse,
    GetCategoryTreeResponse,
} from './responses';

type CategoriesServiceOptions = {
    baseUrl: string;
};

export class CategoriesService implements ICategoriesService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: CategoriesServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public getCategoryList(): Promise<ApiResponse<GetCategoryListResponse>> {
        const url = CategoriesEndpoints.list();
        return makeApiRequest<GetCategoryListResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public async getCategoryDetails(): Promise<
        ApiResponse<GetCategoryDetailsResponse>
    > {
        const url = CategoriesEndpoints.details();
        const result = await makeApiRequest<GetCategoryDetailsResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
        return result.success
            ? {
                  success: true,
                  response: {
                      ...result.response,
                      // BOS-162: the one edge where a category's cost and sale
                      // totals cross into integer tetri.
                      items: (result.response.items ?? []).map((item) => ({
                          ...item,
                          totalCostTetri: tetriFromDecimal(item.totalCost),
                          totalSalePriceTetri: tetriFromDecimal(
                              item.totalSalePrice,
                          ),
                      })),
                  },
              }
            : result;
    }

    public getCategoryTree(): Promise<ApiResponse<GetCategoryTreeResponse>> {
        const url = CategoriesEndpoints.tree();
        return makeApiRequest<GetCategoryTreeResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public getCategoryPicker(): Promise<
        ApiResponse<GetCategoryPickerResponse>
    > {
        // BOS-83: every non-deleted category, flat + unpaginated — for
        // dropdowns and import name-matching that need the whole set at once.
        const url = CategoriesEndpoints.picker();
        return makeApiRequest<GetCategoryPickerResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public createCategory(
        body: CategoryCreateRequest,
    ): Promise<ApiResponse<CategoryWriteResponse>> {
        const url = CategoriesEndpoints.create();

        // bos-api binds [FromForm] on POST /categories — name, slug, and an
        // optional `file`. Packing as multipart lets the server validate the
        // image and persist the category in one round-trip (the alternative
        // was POST /images + POST /categories + PUT /categories/{id}).
        const formData = new FormData();
        formData.append('name', body.name);
        formData.append('slug', body.slug);
        if (body.parentId !== null) {
            formData.append('parentId', body.parentId);
        }
        if (body.imageUpload) {
            formData.append(
                'file',
                base64ToBlob(
                    body.imageUpload.dataBase64,
                    body.imageUpload.mimeType,
                ),
                body.imageUpload.fileName,
            );
        }

        return makeApiRequest<CategoryWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).post(url, formData),
        );
    }

    public updateCategory(
        id: string,
        body: CategoryWriteRequest,
    ): Promise<ApiResponse<CategoryWriteResponse>> {
        const url = CategoriesEndpoints.update(id);
        return makeApiRequest<CategoryWriteResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
    }

    public deleteCategory(id: string): Promise<ApiResponse<void>> {
        const url = CategoriesEndpoints.delete(id);
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(url),
        );
    }

    public getCategoryAttributes(
        id: string,
    ): Promise<ApiResponse<GetCategoryAttributesResponse>> {
        const url = CategoriesEndpoints.attributes(id);
        return makeApiRequest<GetCategoryAttributesResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
    }

    public putCategoryAttributes(
        id: string,
        body: CategoryAttributesWriteRequest,
    ): Promise<ApiResponse<GetCategoryAttributesResponse>> {
        const url = CategoriesEndpoints.attributes(id);
        return makeApiRequest<GetCategoryAttributesResponse>(
            getAxiosClient({ baseURL: this.baseUrl }).put(url, body),
        );
    }
}
